import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from '@arco-design/web-react';
import { beginFormatInteraction, endFormatInteraction } from '@bay-email/editor';
import { ToolItem } from '../ToolItem';

export interface BlockAttributeToolProps {
	title: string;
	icon: React.ReactNode;
	children: React.ReactNode;
	getPopupContainer: () => HTMLElement;
}

// Block-attribute control, not an inline command: an inline span for these is
// unreliable in email clients. The child field writes ${focusIdx}.attributes.*
// through the Form context, which reaches the popover across the portal.
export function BlockAttributeTool({
	title,
	icon,
	children,
	getPopupContainer,
}: BlockAttributeToolProps) {
	const [visible, setVisible] = useState(false);
	const guardHeldRef = useRef(false);

	const release = useCallback(() => {
		if (!guardHeldRef.current) return;
		endFormatInteraction();
		guardHeldRef.current = false;
	}, []);

	// Hold the format guard while open, like MergeTags, so the canvas does not
	// rebuild under the editor's selection.
	const onVisibleChange = useCallback(
		(next: boolean) => {
			if (next && !guardHeldRef.current) {
				beginFormatInteraction();
				guardHeldRef.current = true;
			}
			if (!next) release();
			setVisible(next);
		},
		[release],
	);

	useEffect(() => release, [release]);

	return (
		<Popover
			trigger='click'
			color='#fff'
			position='bl'
			className='bay-email-extensions-Tools-Popover'
			popupVisible={visible}
			onVisibleChange={onVisibleChange}
			getPopupContainer={getPopupContainer}
			content={
				<div
					style={{ width: 240, padding: '12px 12px 0' }}
					onMouseDown={e => e.stopPropagation()}
				>
					{children}
				</div>
			}
		>
			<ToolItem title={title} icon={icon} />
		</Popover>
	);
}
